/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */

const WEBLLM_CDN_URL = 'https://esm.run/@mlc-ai/web-llm';
/**
 * localLLM.js — Motore AI LOCALE per la trascrizione clinica → SOAP.
 *
 * Nessuna chiamata a servizi cloud a pagamento. Tre provider, scelti in
 * automatico (o forzati dall'utente) in quest'ordine:
 *
 *   1. `ollama`  — endpoint locale (default http://localhost:11434) con un
 *                  modello leggero (es. llama3.2:3b, phi3:mini). Streaming NDJSON.
 *   2. `webllm`  — @mlc-ai/web-llm caricato on-demand nel browser (WebGPU).
 *                  Il modello (es. Phi-3-mini) gira interamente sul device.
 *   3. `rules`   — motore deterministico offline (soapParser) con streaming
 *                  simulato: funziona SEMPRE, anche senza GPU/rete.
 *
 * Stato osservabile (per l'indicatore UI):
 *   status ∈ 'idle' | 'loading' | 'ready' | 'processing' | 'error'
 *   provider ∈ 'ollama' | 'webllm' | 'rules'
 *   progress (0-1, solo per il download del modello WebLLM)
 *
 * Uso:
 *   import { localLLM } from '@/lib/localLLM';
 *   const unsub = localLLM.subscribe((state) => ...);
 *   await localLLM.init();
 *   const { soap } = await localLLM.generateSoap(transcript, { onChunk });
 *
 * Il risultato viene salvato dal chiamante con `saveMedicalRecordOffline()`
 * (vedi offlineDb.js) — questo modulo è puro e non tocca lo storage.
 */
import { feedSoapChunk, emptySoapState, formatSoapAsText } from '@/lib/soapParser';

const STORAGE_KEY = 'sanitanexus.localai.config';

export const LOCAL_AI_PROVIDERS = Object.freeze({
  auto: { id: 'auto', label: 'Automatico (Ollama → WebLLM → Regole)' },
  ollama: { id: 'ollama', label: 'Ollama (server locale)' },
  webllm: { id: 'webllm', label: 'WebLLM (nel browser, WebGPU)' },
  rules: { id: 'rules', label: 'Motore a regole (sempre disponibile)' },
});

export const DEFAULT_LOCAL_AI_CONFIG = Object.freeze({
  provider: 'auto',
  ollamaUrl: 'http://localhost:11434',
  ollamaModel: 'llama3.2:3b',
  webllmModel: 'Phi-3-mini-4k-instruct-q4f16_1-MLC',
  // Streaming simulato del motore a regole (ms per token) — feedback visivo.
  rulesTokenDelayMs: 14,
});

/** Prompt strutturato (italiano) per l'estrazione SOAP in JSON. */
export const SOAP_SYSTEM_PROMPT = `Sei un assistente clinico. Ricevi la trascrizione (in italiano) di una visita o di un diario vocale e devi compilare una cartella clinica secondo lo schema SOAP.
Rispondi ESCLUSIVAMENTE con un oggetto JSON valido con queste chiavi (stringhe, italiano, sintetiche, senza inventare dati assenti):
{"subjective": "sintomi riferiti, anamnesi, durata", "objective": "esame obiettivo, parametri vitali, misurazioni", "assessment": "valutazione / ipotesi diagnostica", "plan": "terapia, esami, follow-up, indicazioni"}
Se un'informazione non è presente nella trascrizione, lascia la stringa vuota. Non aggiungere testo fuori dal JSON.`;

// ---------------------------------------------------------------------------
// Config persistence
// ---------------------------------------------------------------------------

export const loadLocalAIConfig = () => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    return raw ? { ...DEFAULT_LOCAL_AI_CONFIG, ...JSON.parse(raw) } : { ...DEFAULT_LOCAL_AI_CONFIG };
  } catch (e) {
    return { ...DEFAULT_LOCAL_AI_CONFIG };
  }
};

export const saveLocalAIConfig = (cfg) => {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg)); } catch (e) { /* ignore */ }
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMPTY_SOAP = () => ({ subjective: '', objective: '', assessment: '', plan: '' });

/** Estrae il primo oggetto JSON valido da un testo LLM (tollerante a ```json). */
export const parseSoapJson = (raw) => {
  if (!raw) return null;
  const cleaned = String(raw).replace(/```json|```/gi, '').trim();
  const start = cleaned.indexOf('{');
  const end = cleaned.lastIndexOf('}');
  if (start === -1 || end === -1 || end <= start) return null;
  try {
    const obj = JSON.parse(cleaned.slice(start, end + 1));
    const out = EMPTY_SOAP();
    for (const k of Object.keys(out)) {
      if (typeof obj[k] === 'string') out[k] = obj[k].trim();
      else if (Array.isArray(obj[k])) out[k] = obj[k].join('; ');
    }
    return out;
  } catch (e) {
    return null;
  }
};

/** Motore deterministico: sentence-split + keyword routing (soapParser). */
export const rulesBasedSoap = (transcript) => {
  const sentences = String(transcript || '')
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?;])\s+|\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  let state = emptySoapState();
  for (const s of sentences) {
    state = feedSoapChunk(state, s, { lang: 'auto' }).state;
  }
  // Se non è stata riconosciuta nessuna sezione, tutto va nel Soggettivo.
  if (!state.subjective && !state.objective && !state.assessment && !state.plan) {
    state = { ...state, subjective: String(transcript || '').trim() };
  }
  return {
    subjective: state.subjective || '',
    objective: state.objective || '',
    assessment: state.assessment || '',
    plan: state.plan || '',
  };
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ---------------------------------------------------------------------------
// Engine (singleton, observable)
// ---------------------------------------------------------------------------

class LocalLLMEngine {
  constructor() {
    this.config = loadLocalAIConfig();
    this.state = {
      status: 'idle',
      provider: null,
      model: null,
      progress: 0,
      error: null,
      lastLatencyMs: null,
    };
    this.listeners = new Set();
    this.webllmEngine = null;
    this._initPromise = null;
  }

  // ----- observable -----
  subscribe(fn) {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  getState() { return this.state; }

  _set(patch) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((fn) => { try { fn(this.state); } catch (e) { /* ignore */ } });
  }

  setConfig(patch) {
    this.config = { ...this.config, ...patch };
    saveLocalAIConfig(this.config);
    // Cambio provider/modello → re-init lazy
    this.webllmEngine = null;
    this._initPromise = null;
    this._set({ status: 'idle', provider: null, model: null, progress: 0, error: null });
  }

  getConfig() { return this.config; }

  // ----- init -----
  async init() {
    if (this._initPromise) return this._initPromise;
    this._initPromise = this._doInit().catch((e) => {
      // Qualunque errore → fallback su regole (mai bloccare il medico).
      this._set({ status: 'ready', provider: 'rules', model: 'regole-it', progress: 1, error: e?.message || String(e) });
    });
    return this._initPromise;
  }

  async _doInit() {
    const want = this.config.provider || 'auto';
    this._set({ status: 'loading', progress: 0, error: null });

    if (want === 'rules') {
      this._set({ status: 'ready', provider: 'rules', model: 'regole-it', progress: 1 });
      return;
    }

    if (want === 'auto' || want === 'ollama') {
      const ok = await this._probeOllama();
      if (ok) {
        this._set({ status: 'ready', provider: 'ollama', model: this.config.ollamaModel, progress: 1 });
        return;
      }
      if (want === 'ollama') throw new Error('Ollama non raggiungibile su ' + this.config.ollamaUrl);
    }

    if (want === 'auto' || want === 'webllm') {
      const hasWebGPU = typeof navigator !== 'undefined' && !!navigator.gpu;
      if (hasWebGPU) {
        try {
          await this._loadWebLLM();
          this._set({ status: 'ready', provider: 'webllm', model: this.config.webllmModel, progress: 1 });
          return;
        } catch (e) {
          if (want === 'webllm') throw e;
        }
      } else if (want === 'webllm') {
        throw new Error('WebGPU non disponibile in questo browser');
      }
    }

    // Fallback finale
    this._set({ status: 'ready', provider: 'rules', model: 'regole-it', progress: 1 });
  }

  async _probeOllama() {
    // In modalità `auto` evitiamo di ritentare la connessione a ogni pagina quando
    // Ollama non è installato (rumore ERR_CONNECTION_REFUSED in console + latenza):
    // un esito negativo viene ricordato per 10 minuti nella sessione.
    const PROBE_KEY = 'sn_ollama_probe_failed_at';
    const auto = (this.config.provider || 'auto') === 'auto';
    try {
      if (auto && typeof sessionStorage !== 'undefined') {
        const failedAt = Number(sessionStorage.getItem(PROBE_KEY) || 0);
        if (failedAt && Date.now() - failedAt < 10 * 60 * 1000) return false;
      }
    } catch (e) { /* storage non disponibile */ }
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 1500);
      const r = await fetch(`${this.config.ollamaUrl.replace(/\/$/, '')}/api/tags`, { signal: ctrl.signal });
      clearTimeout(t);
      if (r.ok) { try { sessionStorage.removeItem(PROBE_KEY); } catch (e) { /* ignore */ } }
      return r.ok;
    } catch (e) {
      try { if (auto) sessionStorage.setItem(PROBE_KEY, String(Date.now())); } catch (err) { /* ignore */ }
      return false;
    }
  }

  async _loadWebLLM() {
    // Import dinamico da CDN: il pacchetto (~MB) non viene incluso nel bundle
    // e viene scaricato solo se il medico abilita WebLLM. URL costante (non
    // controllato dall'utente); `webpackIgnore` lascia al browser l'import ESM nativo.
    const mod = await import(/* webpackIgnore: true */ WEBLLM_CDN_URL);
    const engine = await mod.CreateMLCEngine(this.config.webllmModel, {
      initProgressCallback: (p) => this._set({ status: 'loading', provider: 'webllm', model: this.config.webllmModel, progress: Math.max(0, Math.min(1, p?.progress || 0)) }),
    });
    this.webllmEngine = engine;
  }

  // ----- generation -----
  /**
   * @param {string} transcript
   * @param {{ onChunk?: (partialText: string, partialSoap: object|null) => void, signal?: AbortSignal }} opts
   * @returns {Promise<{ soap: object, raw: string, provider: string, latencyMs: number }>}
   */
  async generateSoap(transcript, opts = {}) {
    await this.init();
    const started = performance.now();
    this._set({ status: 'processing' });
    try {
      let result;
      if (this.state.provider === 'ollama') result = await this._genOllama(transcript, opts);
      else if (this.state.provider === 'webllm') result = await this._genWebLLM(transcript, opts);
      else result = await this._genRules(transcript, opts);
      const latencyMs = Math.round(performance.now() - started);
      this._set({ status: 'ready', lastLatencyMs: latencyMs, error: null });
      return { ...result, provider: this.state.provider, latencyMs };
    } catch (e) {
      if (e?.name === 'AbortError') { this._set({ status: 'ready' }); throw e; }
      // Fallback trasparente: mai lasciare il medico senza output.
      const result = await this._genRules(transcript, opts);
      const latencyMs = Math.round(performance.now() - started);
      this._set({ status: 'ready', provider: 'rules', model: 'regole-it', lastLatencyMs: latencyMs, error: e?.message || String(e) });
      return { ...result, provider: 'rules', latencyMs };
    }
  }

  async _genRules(transcript, { onChunk, signal } = {}) {
    const soap = rulesBasedSoap(transcript);
    const full = formatSoapAsText({ ...emptySoapState(), ...soap });
    // Streaming simulato parola-per-parola per feedback visivo coerente.
    const tokens = full.split(/(\s+)/);
    let acc = '';
    for (const tk of tokens) {
      if (signal?.aborted) { const err = new Error('aborted'); err.name = 'AbortError'; throw err; }
      acc += tk;
      onChunk?.(acc, null);
      if (tk.trim()) await sleep(this.config.rulesTokenDelayMs);
    }
    onChunk?.(full, soap);
    return { soap, raw: full };
  }

  async _genOllama(transcript, { onChunk, signal } = {}) {
    const url = `${this.config.ollamaUrl.replace(/\/$/, '')}/api/generate`;
    const body = {
      model: this.config.ollamaModel,
      prompt: `${SOAP_SYSTEM_PROMPT}\n\nTRASCRIZIONE:\n${transcript}\n\nJSON:`,
      stream: true,
      format: 'json',
      options: { temperature: 0.1 },
    };
    const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal });
    if (!r.ok || !r.body) throw new Error(`Ollama HTTP ${r.status}`);
    const reader = r.body.getReader();
    const decoder = new TextDecoder();
    let raw = '';
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const j = JSON.parse(line);
          if (j.response) { raw += j.response; onChunk?.(raw, parseSoapJson(raw)); }
        } catch (e) { /* partial line */ }
      }
    }
    const soap = parseSoapJson(raw) || rulesBasedSoap(transcript);
    onChunk?.(raw, soap);
    return { soap, raw };
  }

  async _genWebLLM(transcript, { onChunk, signal } = {}) {
    if (!this.webllmEngine) await this._loadWebLLM();
    const chunks = await this.webllmEngine.chat.completions.create({
      stream: true,
      temperature: 0.1,
      messages: [
        { role: 'system', content: SOAP_SYSTEM_PROMPT },
        { role: 'user', content: `TRASCRIZIONE:\n${transcript}` },
      ],
    });
    let raw = '';
    for await (const c of chunks) {
      if (signal?.aborted) { const err = new Error('aborted'); err.name = 'AbortError'; throw err; }
      const delta = c?.choices?.[0]?.delta?.content || '';
      if (delta) { raw += delta; onChunk?.(raw, parseSoapJson(raw)); }
    }
    const soap = parseSoapJson(raw) || rulesBasedSoap(transcript);
    onChunk?.(raw, soap);
    return { soap, raw };
  }
}

export const localLLM = new LocalLLMEngine();

/** Etichette UI per lo stato del motore. */
export const LOCAL_AI_STATUS_LABELS = Object.freeze({
  idle: 'Modello non caricato',
  loading: 'Caricamento modello…',
  ready: 'Modello pronto',
  processing: 'In elaborazione…',
  error: 'Errore modello',
});

export default localLLM;
